import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Receipt, 
  Printer, 
  Download, 
  Edit2, 
  Check, 
  X, 
  Plus,
  Trash2,
  User,
  Calendar,
  Clock,
  Package,
  Pill,
  ShoppingBag,
  Search
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Modal from '../components/Modal';
import { appointments, patients, doctors } from '../data/mockData';

// Productos del inventario (mismo que en Inventario.js)
const inventoryProducts = [
  { id: 1, name: 'Paracetamol 500mg', type: 'medicamento', price: 45, stock: 120 },
  { id: 2, name: 'Ibuprofeno 400mg', type: 'medicamento', price: 65, stock: 8 },
  { id: 3, name: 'Vitamina C 1000mg', type: 'medicamento', price: 180, stock: 45 },
  { id: 4, name: 'Termómetro Digital', type: 'producto', price: 150, stock: 15 },
  { id: 5, name: 'Oxímetro de Pulso', type: 'producto', price: 450, stock: 3 },
  { id: 6, name: 'Baumanómetro Manual', type: 'producto', price: 380, stock: 8 },
];

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 100px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const ReciboCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ReciboHeader = styled.div`
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 2px dashed ${({ theme }) => theme.colors.border};
  margin-bottom: 20px;
`;

const ClinicName = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const ReciboNumber = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ServiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  gap: 12px;
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const ServiceDuration = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriceInput = styled.input`
  width: 100px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PriceDisplay = styled.span`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
  min-width: 80px;
  text-align: right;
`;

const EditButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DeleteButton = styled(EditButton)`
  &:hover {
    color: ${({ theme }) => theme.colors.dangerText};
  }
`;

const AddServiceButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TotalSection = styled.div`
  border-top: 2px dashed ${({ theme }) => theme.colors.border};
  margin-top: 20px;
  padding-top: 20px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: ${({ $isTotal }) => $isTotal ? '18px' : '14px'};
  font-weight: ${({ $isTotal, theme }) => $isTotal ? theme.fontWeights.bold : theme.fontWeights.normal};
  color: ${({ $isTotal, theme }) => $isTotal ? theme.colors.text : theme.colors.textSecondary};
`;

const TotalValue = styled.span`
  color: ${({ $isTotal, theme }) => $isTotal ? theme.colors.primary : theme.colors.text};
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const AddServiceModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  flex: 1;
`;

const FormField = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  gap: 12px;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(ModalButton)`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
`;

const SaveButton = styled(ModalButton)`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  color: white;
`;

const ModalTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModalTab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.background};
  color: ${({ $active, theme }) => $active ? 'white' : theme.colors.text};
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProductGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const ProductIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ $type, theme }) => $type === 'medicamento' ? `${theme.colors.info}20` : `${theme.colors.primary}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $type, theme }) => $type === 'medicamento' ? theme.colors.info : theme.colors.primary};
  }
`;

const ProductItemInfo = styled.div`
  flex: 1;
`;

const ProductItemName = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ProductItemDetails = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProductItemPrice = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SearchInput = styled.div`
  position: relative;
  margin-bottom: 12px;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 8px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.textSecondary};
    }
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  overflow-x: auto;
  width: 100%;
`;

const FilterTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 16px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.white};
  color: ${({ $active, theme }) => $active ? 'white' : theme.colors.text};

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primaryDark : theme.colors.gray};
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  padding: 6px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 14px;
`;

const GenerarRecibo = () => {
  const { citaId } = useParams();
  const navigate = useNavigate();
  
  const appointment = appointments.find(apt => apt.id === parseInt(citaId));
  const patient = patients.find(p => p.name === appointment?.patient);
  const doctor = doctors.find(d => d.name === appointment?.doctor);
  
  const [services, setServices] = useState(
    appointment?.services?.map(s => ({ ...s, editingPrice: false, isProduct: false })) || []
  );
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newService, setNewService] = useState({ name: '', price: '', duration: '' });
  const [modalTab, setModalTab] = useState('servicio');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [productSearch, setProductSearch] = useState('');
  const [productFilter, setProductFilter] = useState('todos');
  
  // Filtrar productos por búsqueda y tipo
  const filteredInventoryProducts = inventoryProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(productSearch.toLowerCase());
    const matchesType = productFilter === 'todos' || product.type === productFilter;
    return matchesSearch && matchesType;
  });
  
  // Generar número de recibo
  const reciboNumber = `REC-${new Date().getFullYear()}-${String(appointment?.id || 0).padStart(5, '0')}`;
  
  // Combinar servicios y productos para el total
  const allItems = [...services, ...products];
  
  // Calcular totales
  const subtotal = allItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const iva = 0; // Sin IVA por ahora
  const total = subtotal + iva;
  
  const handlePriceChange = (index, newPrice) => {
    const updated = [...services];
    updated[index].price = parseFloat(newPrice) || 0;
    setServices(updated);
  };
  
  const toggleEditPrice = (index) => {
    const updated = [...services];
    updated[index].editingPrice = !updated[index].editingPrice;
    setServices(updated);
  };
  
  const handleDeleteService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };
  
  const handleDeleteProduct = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };
  
  const handleAddService = () => {
    if (newService.name && newService.price) {
      setServices([
        ...services,
        {
          id: Date.now(),
          name: newService.name,
          price: parseFloat(newService.price) || 0,
          duration: parseInt(newService.duration) || 30,
          editingPrice: false
        }
      ]);
      setNewService({ name: '', price: '', duration: '' });
      setShowAddModal(false);
    }
  };
  
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setProductQuantity(1);
  };
  
  const handleAddProduct = () => {
    if (selectedProduct) {
      setProducts([
        ...products,
        {
          id: Date.now(),
          productId: selectedProduct.id,
          name: selectedProduct.name,
          type: selectedProduct.type,
          price: selectedProduct.price,
          quantity: productQuantity,
          editingPrice: false
        }
      ]);
      setSelectedProduct(null);
      setProductQuantity(1);
      setShowAddModal(false);
    }
  };
  
  const handleProductPriceChange = (index, newPrice) => {
    const updated = [...products];
    updated[index].price = parseFloat(newPrice) || 0;
    setProducts(updated);
  };
  
  const toggleProductEditPrice = (index) => {
    const updated = [...products];
    updated[index].editingPrice = !updated[index].editingPrice;
    setProducts(updated);
  };
  
  const handleOpenModal = () => {
    setModalTab('servicio');
    setSelectedProduct(null);
    setNewService({ name: '', price: '', duration: '' });
    setProductSearch('');
    setProductFilter('todos');
    setShowAddModal(true);
  };
  
  const handleGenerateRecibo = () => {
    // Aquí iría la lógica para guardar/imprimir el recibo
    setShowSuccessModal(true);
  };
  
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate(`/detalle-cita/${citaId}`);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  if (!appointment) {
    return (
      <PageContainer>
        <Header title="Generar Recibo" showBack />
        <Content>
          <ReciboCard>
            <p>Cita no encontrada</p>
          </ReciboCard>
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Modal
        isOpen={showSuccessModal}
        onClose={handleSuccessClose}
        title="Recibo Generado"
        message="El recibo ha sido generado exitosamente."
        type="success"
        confirmText="Aceptar"
      />
      
      <Header title="Generar Recibo" showBack />
      
      <Content>
        <ReciboCard>
          <ReciboHeader>
            <ClinicName>Dr. Desk Clínica</ClinicName>
            <ReciboNumber>{reciboNumber}</ReciboNumber>
          </ReciboHeader>
          
          <InfoSection>
            <InfoRow>
              <User />
              <span><strong>Paciente:</strong> {appointment.patient}</span>
            </InfoRow>
            <InfoRow>
              <Calendar />
              <span><strong>Fecha:</strong> {formatDate(appointment.date)}</span>
            </InfoRow>
            <InfoRow>
              <Clock />
              <span><strong>Hora:</strong> {appointment.time}</span>
            </InfoRow>
          </InfoSection>
          
          <SectionTitle>
            Servicios
          </SectionTitle>
          
          <ServicesList>
            {services.map((service, index) => (
              <ServiceRow key={service.id || index}>
                <ServiceInfo>
                  <ServiceName>{service.name}</ServiceName>
                  <ServiceDuration>{service.duration} min</ServiceDuration>
                </ServiceInfo>
                <PriceContainer>
                  {service.editingPrice ? (
                    <>
                      <PriceInput
                        type="number"
                        value={service.price}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        autoFocus
                      />
                      <EditButton onClick={() => toggleEditPrice(index)}>
                        <Check />
                      </EditButton>
                    </>
                  ) : (
                    <>
                      <PriceDisplay>${service.price}</PriceDisplay>
                      <EditButton onClick={() => toggleEditPrice(index)}>
                        <Edit2 />
                      </EditButton>
                    </>
                  )}
                  <DeleteButton onClick={() => handleDeleteService(index)}>
                    <Trash2 />
                  </DeleteButton>
                </PriceContainer>
              </ServiceRow>
            ))}
          </ServicesList>
          
          {products.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: '20px' }}>
                Productos
              </SectionTitle>
              
              <ServicesList>
                {products.map((product, index) => (
                  <ServiceRow key={product.id || index}>
                    <ProductIcon $type={product.type} style={{ width: '36px', height: '36px', marginRight: '8px' }}>
                      {product.type === 'medicamento' ? <Pill /> : <ShoppingBag />}
                    </ProductIcon>
                    <ServiceInfo>
                      <ServiceName>{product.name}</ServiceName>
                      <ServiceDuration>Cantidad: {product.quantity}</ServiceDuration>
                    </ServiceInfo>
                    <PriceContainer>
                      {product.editingPrice ? (
                        <>
                          <PriceInput
                            type="number"
                            value={product.price}
                            onChange={(e) => handleProductPriceChange(index, e.target.value)}
                            autoFocus
                          />
                          <EditButton onClick={() => toggleProductEditPrice(index)}>
                            <Check />
                          </EditButton>
                        </>
                      ) : (
                        <>
                          <PriceDisplay>${(product.price * product.quantity).toFixed(2)}</PriceDisplay>
                          <EditButton onClick={() => toggleProductEditPrice(index)}>
                            <Edit2 />
                          </EditButton>
                        </>
                      )}
                      <DeleteButton onClick={() => handleDeleteProduct(index)}>
                        <Trash2 />
                      </DeleteButton>
                    </PriceContainer>
                  </ServiceRow>
                ))}
              </ServicesList>
            </>
          )}
          
          <AddServiceButton onClick={handleOpenModal}>
            <Plus />
            Agregar Servicio o Producto
          </AddServiceButton>
          
          <TotalSection>
            <TotalRow>
              <span>Subtotal</span>
              <TotalValue>${subtotal.toFixed(2)}</TotalValue>
            </TotalRow>
            {iva > 0 && (
              <TotalRow>
                <span>IVA (16%)</span>
                <TotalValue>${iva.toFixed(2)}</TotalValue>
              </TotalRow>
            )}
            <TotalRow $isTotal>
              <span>Total</span>
              <TotalValue $isTotal>${total.toFixed(2)}</TotalValue>
            </TotalRow>
          </TotalSection>
        </ReciboCard>
        
        <ActionsContainer>
          <PrimaryButton onClick={handleGenerateRecibo}>
            <Printer />
            Imprimir Recibo
          </PrimaryButton>
          <SecondaryButton>
            <Download />
            Descargar PDF
          </SecondaryButton>
        </ActionsContainer>
      </Content>
      
      {/* Modal para agregar servicio o producto */}
      {showAddModal && (
        <AddServiceModal onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Agregar al Recibo</ModalTitle>
              <CloseButton onClick={() => setShowAddModal(false)}>
                <X />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalTabs>
                <ModalTab $active={modalTab === 'servicio'} onClick={() => setModalTab('servicio')}>
                  <Receipt />
                  Servicio
                </ModalTab>
                <ModalTab $active={modalTab === 'producto'} onClick={() => setModalTab('producto')}>
                  <Package />
                  Producto
                </ModalTab>
              </ModalTabs>
              
              {modalTab === 'servicio' ? (
                <>
                  <FormField>
                    <Label>Nombre del Servicio</Label>
                    <Input
                      type="text"
                      placeholder="Ej: Consulta General"
                      value={newService.name}
                      onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                    />
                  </FormField>
                  <FormField>
                    <Label>Precio ($)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={newService.price}
                      onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                    />
                  </FormField>
                  <FormField>
                    <Label>Duración (minutos)</Label>
                    <Input
                      type="number"
                      placeholder="30"
                      value={newService.duration}
                      onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                    />
                  </FormField>
                </>
              ) : (
                <>
                  <FormField>
                    <Label>Buscar Producto</Label>
                    <SearchInput>
                      <Search />
                      <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                      />
                    </SearchInput>
                    <FilterTabs>
                      <FilterTab $active={productFilter === 'todos'} onClick={() => setProductFilter('todos')}>
                        <Package /> Todos
                      </FilterTab>
                      <FilterTab $active={productFilter === 'medicamento'} onClick={() => setProductFilter('medicamento')}>
                        <Pill /> Medicamentos
                      </FilterTab>
                      <FilterTab $active={productFilter === 'producto'} onClick={() => setProductFilter('producto')}>
                        <ShoppingBag /> Productos
                      </FilterTab>
                    </FilterTabs>
                    <ProductGrid>
                      {filteredInventoryProducts.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                          No se encontraron productos
                        </div>
                      ) : (
                        filteredInventoryProducts.map(product => (
                          <ProductItem 
                            key={product.id}
                            onClick={() => handleSelectProduct(product)}
                            style={{
                              border: selectedProduct?.id === product.id ? '2px solid #4F46E5' : 'none'
                            }}
                          >
                            <ProductIcon $type={product.type}>
                              {product.type === 'medicamento' ? <Pill /> : <ShoppingBag />}
                            </ProductIcon>
                            <ProductItemInfo>
                              <ProductItemName>{product.name}</ProductItemName>
                              <ProductItemDetails>
                                <ProductItemPrice>${product.price}</ProductItemPrice>
                                <span>Stock: {product.stock}</span>
                              </ProductItemDetails>
                            </ProductItemInfo>
                          </ProductItem>
                        ))
                      )}
                    </ProductGrid>
                  </FormField>
                  
                  {selectedProduct && (
                    <FormField>
                      <Label>Cantidad</Label>
                      <QuantityControl>
                        <QuantityButton onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}>
                          -
                        </QuantityButton>
                        <QuantityInput
                          type="number"
                          value={productQuantity}
                          onChange={(e) => setProductQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          min="1"
                        />
                        <QuantityButton onClick={() => setProductQuantity(productQuantity + 1)}>
                          +
                        </QuantityButton>
                        <span style={{ marginLeft: '12px', fontWeight: '600', color: '#4F46E5' }}>
                          Total: ${(selectedProduct.price * productQuantity).toFixed(2)}
                        </span>
                      </QuantityControl>
                    </FormField>
                  )}
                </>
              )}
            </ModalBody>
            <ModalActions>
              <CancelButton onClick={() => setShowAddModal(false)}>
                Cancelar
              </CancelButton>
              <SaveButton onClick={modalTab === 'servicio' ? handleAddService : handleAddProduct}>
                Agregar
              </SaveButton>
            </ModalActions>
          </ModalContent>
        </AddServiceModal>
      )}
    </PageContainer>
  );
};

export default GenerarRecibo;
